import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ClipboardCheck, Flag, CalendarDays, ExternalLink, Printer } from "lucide-react";
import { api } from "@/lib/api";
import { STATUS_MAP, PRIORITY_MAP, PRIORITIES, isOverdue, fmtDate } from "@/lib/taskConstants";
import { SpaceAvatar } from "@/components/Avatars";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function MyTasksPage() {
  const [statusFilter, setStatusFilter] = useState("open");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [overdueOnly, setOverdueOnly] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["my-tasks"],
    queryFn: async () => (await api.get("/me/assigned-tasks")).data,
  });

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-8 py-10 space-y-4">
        <Skeleton className="h-16 rounded-xl" /><Skeleton className="h-48 rounded-xl" />
      </div>
    );
  }

  const tasks = (data?.tasks || []).filter((t) => {
    if (statusFilter === "open" && t.status === "done") return false;
    if (statusFilter === "done" && t.status !== "done") return false;
    if (priorityFilter !== "all" && t.priority !== priorityFilter) return false;
    if (overdueOnly && !isOverdue(t)) return false;
    return true;
  });
  const wsMap = Object.fromEntries((data?.workspaces || []).map((w) => [w.id, w]));

  const groups = [];
  for (const t of tasks) {
    let g = groups.find((x) => x.wsId === t.workspace_id);
    if (!g) { g = { wsId: t.workspace_id, projects: [] }; groups.push(g); }
    let p = g.projects.find((x) => x.id === t.project_id);
    if (!p) { p = { id: t.project_id, name: t.project_name, icon: t.project_icon, tasks: [] }; g.projects.push(p); }
    p.tasks.push(t);
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-8 sm:py-10 space-y-6" data-testid="my-tasks-page">
      <div className="flex items-center gap-3 animate-fade-up">
        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <ClipboardCheck className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Tareas asignadas a mí</h1>
          <p className="text-xs text-muted-foreground">{tasks.length} {tasks.length === 1 ? "tarea" : "tareas"}</p>
        </div>
        <Button variant="outline" size="sm" className="ml-auto h-8 text-xs print:hidden" onClick={() => window.print()} data-testid="my-tasks-print-btn">
          <Printer className="h-3.5 w-3.5 mr-1.5" /> Imprimir / PDF
        </Button>
      </div>
      <p className="hidden print:block text-xs text-muted-foreground -mt-3">
        nuxxum · Impreso el {new Date().toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" })}
      </p>

      <div className="flex flex-wrap items-center gap-3 print:hidden" data-testid="my-tasks-filters">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36 h-8 text-xs" data-testid="my-tasks-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="open">Abiertas</SelectItem>
            <SelectItem value="done">Completadas</SelectItem>
            <SelectItem value="all">Todas</SelectItem>
          </SelectContent>
        </Select>
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-36 h-8 text-xs" data-testid="my-tasks-priority-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toda prioridad</SelectItem>
            {PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
          <Checkbox checked={overdueOnly} onCheckedChange={(v) => setOverdueOnly(!!v)} data-testid="my-tasks-overdue-filter" />
          Solo vencidas
        </label>
      </div>

      {groups.length === 0 ? (
        <Card className="p-10 text-center text-sm text-muted-foreground" data-testid="my-tasks-empty">
          No tienes tareas asignadas con estos filtros. 🎉
        </Card>
      ) : groups.map((g) => {
        const ws = wsMap[g.wsId] || {};
        return (
          <Card key={g.wsId} className="p-5 space-y-4 animate-fade-up print:break-inside-avoid print:shadow-none" data-testid={`my-tasks-ws-${g.wsId}`}>
            <div className="flex items-center gap-2.5">
              <SpaceAvatar icon={ws.icon} color={ws.color} size="sm" />
              <h2 className="font-heading font-semibold">{ws.name}</h2>
            </div>
            {g.projects.map((p) => (
              <div key={p.id} className="space-y-1.5">
                <Link to={`/space/${g.wsId}/project/${p.id}`}
                  className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-primary">
                  {p.icon} {p.name} <ExternalLink className="h-3 w-3" />
                </Link>
                {p.tasks.map((t) => (
                  <Link key={t.id} to={`/space/${g.wsId}/project/${p.id}`} data-testid={`my-task-${t.id}`}
                    className="flex items-center gap-2.5 rounded-lg border px-3 py-2 hover:border-primary/50 transition-colors">
                    <span className="h-2 w-2 rounded-full shrink-0"
                      style={{ backgroundColor: isOverdue(t) ? "#D93025" : STATUS_MAP[t.status]?.color }} />
                    <span className={`text-sm flex-1 min-w-0 truncate ${t.status === "done" ? "line-through text-muted-foreground" : ""}`}>
                      {t.parent_task_id ? <span className="text-muted-foreground/60 mr-1">└</span> : null}{t.title}
                    </span>
                    <Flag className="h-3 w-3 shrink-0" style={{ color: PRIORITY_MAP[t.priority]?.color }} />
                    {t.due_date && (
                      <span className={`text-[11px] flex items-center gap-1 shrink-0 ${isOverdue(t) ? "text-destructive font-medium" : "text-muted-foreground"}`}>
                        <CalendarDays className="h-3 w-3" />{fmtDate(t.due_date)}
                      </span>
                    )}
                    <Badge variant="outline" className="text-[10px] font-normal shrink-0">{STATUS_MAP[t.status]?.label}</Badge>
                  </Link>
                ))}
              </div>
            ))}
          </Card>
        );
      })}
    </div>
  );
}
